import {
  getGeneralConfig,
  saveGeneralConfig,
  getHeaderConfig,
  saveHeaderConfig,
  getMenuConfig,
  saveMenuConfig,
  getContactSettings,
  saveContactSettings,
  getCartSettings,
  saveCartSettings,
  getFAQSettings,
  saveFAQSettings,
  createShowcaseArea,
  seedLegalDefaults,
} from '../../../../services/ecommerceApi';
import type { WizardData, WizardStepId } from './wizardTypes';

export type InstallStepStatus = 'pending' | 'running' | 'success' | 'error' | 'skipped';

export interface InstallStepProgress {
  id: WizardStepId;
  label: string;
  status: InstallStepStatus;
  error?: string;
}

export interface InstallStepDef {
  id: WizardStepId;
  label: string;
  enabled: boolean;
  run: () => Promise<void>;
}

const slugify = (value: string): string =>
  (value || '')
    .toLowerCase()
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ş/g, 's')
    .replace(/ü/g, 'u')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'vitrin';

const unwrap = (response: any) => response?.data ?? response ?? {};

/**
 * Sihirbaz taslağından, "Kur" adımında sırayla çalıştırılacak kurulum
 * adımlarını üretir. Her adım mevcut ayarı çekip birleştirerek kaydeder,
 * böylece panelde daha önce girilmiş veriler ezilmez.
 */
export function buildInstallSteps(
  companyId: string,
  configId: string,
  data: WizardData,
): InstallStepDef[] {
  const resolvedConfigId = configId || companyId;

  const steps: InstallStepDef[] = [
    {
      id: 'general',
      label: 'Genel Bilgiler',
      enabled: !data.skipped.general,
      run: async () => {
        const existing = unwrap(await getGeneralConfig(companyId));
        await saveGeneralConfig({
          ...existing,
          id: existing.id || resolvedConfigId,
          companyId,
          siteName: data.general.siteName || existing.siteName,
          faviconUrl: data.general.faviconUrl || existing.faviconUrl,
          primaryColor: data.general.primaryColor,
          secondaryColor: data.general.secondaryColor,
          fontFamily: data.general.fontFamily,
          metaTitle: data.general.metaTitle || existing.metaTitle,
          metaDescription: data.general.metaDescription || existing.metaDescription,
        });
      },
    },
    {
      id: 'theme',
      label: 'Tema Seçimi',
      enabled: !data.skipped.theme,
      run: async () => {
        const existingHeader = unwrap(await getHeaderConfig(companyId));
        await saveHeaderConfig({
          ...existingHeader,
          companyId,
          configId: existingHeader.configId || resolvedConfigId,
          headerStyle: data.theme.headerType,
        });

        const existingGeneral = unwrap(await getGeneralConfig(companyId));
        await saveGeneralConfig({
          ...existingGeneral,
          id: existingGeneral.id || resolvedConfigId,
          companyId,
          layoutType: data.theme.layoutType,
          containerMaxWidth: data.theme.containerMaxWidth,
        });
      },
    },
    {
      id: 'pagesMenu',
      label: 'Sayfalar ve Menü',
      enabled: !data.skipped.pagesMenu,
      run: async () => {
        const existingMenu = unwrap(await getMenuConfig(companyId));
        await saveMenuConfig({
          ...existingMenu,
          companyId,
          configId: existingMenu.configId || resolvedConfigId,
          enabled: data.pagesMenu.menuEnabled,
          showHomePage: data.pagesMenu.showHomePage,
          showAllCategories: data.pagesMenu.showAllCategories,
        });

        // SSS sayfası aktifliği
        await saveFAQSettings({
          configId: resolvedConfigId,
          companyId,
          enabled: data.pagesMenu.faqEnabled,
        }).catch(() => undefined);

        // Destek sayfası aktifliği (genel config bayrağı)
        const existingGeneral = unwrap(await getGeneralConfig(companyId));
        await saveGeneralConfig({
          ...existingGeneral,
          id: existingGeneral.id || resolvedConfigId,
          companyId,
          supportEnabled: data.pagesMenu.supportEnabled,
        });
      },
    },
    {
      id: 'contact',
      label: 'İletişim Bilgileri',
      enabled: !data.skipped.contact,
      run: async () => {
        let existing: any = {};
        try {
          existing = unwrap(await getContactSettings(companyId, resolvedConfigId));
        } catch {
          existing = {};
        }
        await saveContactSettings({
          ...existing,
          companyId,
          configId: resolvedConfigId,
          contactInfoEnabled: true,
          contactAddress: data.contact.address,
          contactPhone: data.contact.phone,
          contactEmail: data.contact.email,
          contactWorkingHours: data.contact.workingHours,
          mapEnabled: data.contact.mapEnabled,
        });
      },
    },
    {
      id: 'products',
      label: 'Ürün ve Vitrin',
      enabled: !data.skipped.products && data.products.createShowcase,
      run: async () => {
        const title = data.products.showcaseTitle || 'Vitrin';
        await createShowcaseArea({
          companyId,
          configId: resolvedConfigId,
          slug: slugify(title),
          title,
          kind: data.products.showcaseKind,
          showOnHomepage: true,
          homepageLimit: data.products.homepageLimit,
          homepageLayout: 'Grid',
          insertAfter: 'after-trust',
          displayOrder: 0,
          showViewAll: true,
          viewAllText: 'Tümünü Gör',
          titleAlign: 'left',
          titleFontSize: 32,
          animation: 'FadeUp',
          isActive: true,
          productIds: [],
        });
      },
    },
    {
      id: 'paymentShipping',
      label: 'Ödeme ve Teslimat',
      enabled: !data.skipped.paymentShipping,
      run: async () => {
        let existing: any = {};
        try {
          existing = unwrap(await getCartSettings(companyId, resolvedConfigId));
        } catch {
          existing = {};
        }

        const shipping = {
          enabled: data.paymentShipping.shippingEnabled,
          freeShippingThreshold: data.paymentShipping.freeShippingThreshold,
          shippingCost: data.paymentShipping.shippingCost,
          currency: '₺',
          displayName: 'Kargo',
        };

        let foodOrderSettings: any = {};
        try {
          foodOrderSettings = existing.foodOrderSettingsJson
            ? JSON.parse(existing.foodOrderSettingsJson)
            : {};
        } catch {
          foodOrderSettings = {};
        }
        foodOrderSettings.guestCheckoutEnabled = data.paymentShipping.guestCheckoutEnabled;

        await saveCartSettings({
          id: existing.id || null,
          configId: resolvedConfigId,
          companyId,
          orderType: existing.orderType || 'product',
          pageTitle: existing.pageTitle || 'Sepetim',
          productCardJson: existing.productCardJson,
          orderSummaryJson: existing.orderSummaryJson,
          shippingJson: JSON.stringify(shipping),
          completeOrderButtonJson: existing.completeOrderButtonJson,
          foodOrderSettingsJson: JSON.stringify(foodOrderSettings),
        });
      },
    },
    {
      id: 'legal',
      label: 'Yasal Metinler',
      enabled: !data.skipped.legal && data.legal.seedDefaults,
      run: async () => {
        await seedLegalDefaults(resolvedConfigId);
      },
    },
  ];

  return steps;
}

/**
 * Kurulum adımlarını sırayla çalıştırır. Bir adım hata alsa da akış devam
 * eder; hatalar ilgili adımda işaretlenir ve toplam başarı durumu döner.
 */
export async function runInstall(
  steps: InstallStepDef[],
  onProgress: (progress: InstallStepProgress[]) => void,
): Promise<{ allSucceeded: boolean; results: InstallStepProgress[] }> {
  const results: InstallStepProgress[] = steps.map((s) => ({
    id: s.id,
    label: s.label,
    status: s.enabled ? 'pending' : 'skipped',
  }));
  onProgress([...results]);

  for (let i = 0; i < steps.length; i += 1) {
    const step = steps[i];
    if (!step.enabled) continue;

    results[i] = { ...results[i], status: 'running', error: undefined };
    onProgress([...results]);

    try {
      await step.run();
      results[i] = { ...results[i], status: 'success' };
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Bilinmeyen bir hata oluştu';
      results[i] = { ...results[i], status: 'error', error: message };
    }
    onProgress([...results]);
  }

  const allSucceeded = results.every((r) => r.status === 'success' || r.status === 'skipped');
  return { allSucceeded, results };
}
